import { useState, useRef, useEffect } from "react";
import { Phone, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

type RevealContactType = "phone" | "email";

interface RevealContactProps {
  /** "phone" or "email" — controls icon, default label, and href scheme. */
  type: RevealContactType;
  /**
   * Base64-encoded full href, e.g. btoa("tel:+17633436300") or
   * btoa("mailto:hello@example.com"). Stored encoded so it doesn't ship as
   * scrapable plaintext in the static HTML/JS-source view.
   */
  encoded: string;
  /** Pre-reveal label. Defaults to "Call Us" / "Email Us". */
  label?: string;
  /** Tailwind classes for the outer button. */
  className?: string;
  /** Tailwind classes for the icon. */
  iconClassName?: string;
  /** Tailwind classes for the helper line ("Click again to call/email"). */
  helperClassName?: string;
  /** Hide the small helper sub-label after reveal. */
  hideHelper?: boolean;
  /** Hide the leading icon. */
  hideIcon?: boolean;
  /**
   * Optional override for the displayed value after reveal. If omitted, the
   * decoded value is formatted automatically (US phone formatting for tel,
   * raw address for mailto).
   */
  displayValue?: string;
}

const decode = (v: string): string => {
  try {
    return typeof window === "undefined" ? "" : window.atob(v);
  } catch {
    return "";
  }
};

/** "+17633436300" -> "(763) 343-6300" */
const formatUSPhone = (telHref: string): string => {
  const digits = telHref.replace(/^tel:/, "").replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return telHref.replace(/^tel:/, "");
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

const formatEmail = (mailHref: string): string => mailHref.replace(/^mailto:/, "");

/**
 * Two-step contact reveal.
 *  - First click: decode + show the value (and helper).
 *  - Second click: navigate to the tel:/mailto: href.
 *
 * Encoded value never appears as plaintext in the static markup. Pre-reveal
 * aria-label is intentionally generic ("Call Hiler Mortgage", etc.) so it
 * doesn't expose the digits before the user explicitly opts in.
 */
const RevealContact = ({
  type,
  encoded,
  label,
  className,
  iconClassName,
  helperClassName,
  hideHelper,
  hideIcon,
  displayValue,
}: RevealContactProps) => {
  const [revealed, setRevealed] = useState(false);
  const [decoded, setDecoded] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Keep keyboard focus on the button after reveal so a second Enter/Space dials.
  useEffect(() => {
    if (revealed) buttonRef.current?.focus({ preventScroll: true });
  }, [revealed]);

  const Icon = type === "phone" ? Phone : Mail;
  const fallbackLabel = type === "phone" ? "Call Us" : "Email Us";
  const helperText = type === "phone" ? "Click again to call" : "Click again to email";
  const ariaPre = type === "phone" ? "Reveal phone number" : "Reveal email address";
  const ariaPost = type === "phone" ? "Call this number" : "Send an email";

  const handleClick = () => {
    if (!revealed) {
      const value = decode(encoded);
      if (!value) return;
      setDecoded(value);
      setRevealed(true);
      // Reveal-only: GA gets contact_reveal; Meta gets a custom event
      // (NOT the standard Contact event — that's reserved for actual taps).
      track("contact_reveal", { name: "ContactReveal", kind: "custom" }, { method: type });
      return;
    }
    const href = decoded || decode(encoded);
    if (href) {
      // Actual phone/email tap → real Contact intent.
      if (type === "phone") {
        track("phone_click", { name: "Contact", kind: "standard" }, { method: "phone" });
      } else {
        track("email_click", { name: "Contact", kind: "standard" }, { method: "email" });
      }
      window.location.href = href;
    }
  };

  const shown = revealed
    ? displayValue ?? (type === "phone" ? formatUSPhone(decoded) : formatEmail(decoded))
    : (label ?? fallbackLabel);

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      aria-label={revealed ? ariaPost : ariaPre}
      aria-live="polite"
      className={cn(
        "inline-flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 text-left",
        className,
      )}
    >
      {!hideIcon && <Icon className={cn("h-4 w-4 shrink-0", iconClassName)} aria-hidden="true" />}
      <span className="inline-flex flex-col leading-tight">
        <span>{shown}</span>
        {revealed && !hideHelper && (
          <span className={cn("text-[11px] opacity-70", helperClassName)}>{helperText}</span>
        )}
      </span>
    </button>
  );
};

export default RevealContact;